import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { SnackbarProvider, closeSnackbar } from "notistack";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./index.css";
import App from "./App";
import { AuthProvider } from "./contexts/AuthContext";
import { LoginModalProvider } from "./contexts/LoginModalContext";
import { ButtonFontProvider } from "./contexts/ButtonFontContext";
import { queryClient } from "./services/queryClient";
import { theme } from "./theme";
import { initializeGA4 } from "./utils/analytics";
import { initializeWebVitals } from "./utils/webVitals";
import { registerServiceWorker } from "./utils/serviceWorker";

// Initialize Google Analytics 4
if (typeof window !== "undefined") {
  initializeGA4();
  // Initialize Core Web Vitals monitoring
  initializeWebVitals();
  // Register Service Worker for PWA/offline support
  registerServiceWorker();
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            dense
            preventDuplicate
            autoHideDuration={5000}
            action={(snackbarId) => (
              <IconButton
                onClick={() => closeSnackbar(snackbarId)}
                size="small"
                sx={{
                  color: "inherit",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.08)",
                  },
                }}
                aria-label="Close"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          >
            <AuthProvider>
              <LoginModalProvider>
                <ButtonFontProvider>
                  <App />
                </ButtonFontProvider>
              </LoginModalProvider>
            </AuthProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);
